import axios from 'axios';
import { action, makeObservable, observable, runInAction } from 'mobx';

import { toast } from '@servicetitan/anvil2';
import { inject, injectable } from '@servicetitan/react-ioc';

import { EstimatesApi } from '../../common/api/estimates.api';
import { InvoicesApi } from '../../common/api/invoices.api';
import { JobsApi } from '../../common/api/jobs.api';
import { EstimateViewModel } from '../../common/estimate-select/models/estimate-view.model';
import { MessageType } from '../../common/models/message-type.model';
import { ChatStore } from '../../common/stores/chat.store';
import { MainStore } from '../../common/stores/main.store';
import { ReplacementPartViewModelWithMatch } from '../models/replacement-part-view-model';

@injectable()
export class ReplacementPartsActionsStore {
    @observable addingToInvoice = false;
    @observable addingToEstimate = false;
    @observable addingNoteToJob = false;

    constructor(
        @inject(MainStore) private readonly mainStore: MainStore,
        @inject(ChatStore) private readonly chatStore: ChatStore,
        @inject(EstimatesApi) private readonly estimatesApi: EstimatesApi,
        @inject(JobsApi) private readonly jobsApi: JobsApi,
        @inject(InvoicesApi) private readonly invoicesApi: InvoicesApi
    ) {
        makeObservable(this);
    }

    @action
    addPartToInvoice = async (item: ReplacementPartViewModelWithMatch) => {
        const invoiceId = this.mainStore.jobDetails?.invoiceId;
        if (!invoiceId) {
            toast.danger({
                title: 'No Invoice Found for selected appointment/job.',
                duration: 4000,
            });
            return;
        }
        const invoice = (this.mainStore.jobDetails?.invoices ?? []).find(x => x.id === invoiceId);
        if (!invoice) {
            toast.danger({
                title: 'No Invoice Found for selected appointment/job.',
                duration: 4000,
            });
            return;
        }

        if (invoice.status === 'Posted') {
            toast.warning({
                title: 'Invoice is posted.',
                duration: 4000,
            });
            return;
        }

        if (!item.match) {
            toast.danger({
                title: 'No Pricebook item found',
                duration: 4000,
            });
            return;
        }

        this.chatStore.enqueueMessages([
            {
                showAvatar: false,
                showBubble: true,
                date: new Date(),
                text: 'Add to Invoice',
                type: MessageType.Text,
                isMine: true,
            },
        ]);

        this.addingToInvoice = true;

        try {
            await this.invoicesApi.addInvoiceItem(invoiceId, {
                id: item.match.id,
            });

            runInAction(() => {
                this.addingToInvoice = false;
            });

            this.chatStore.enqueueMessages([
                {
                    showAvatar: true,
                    showBubble: true,
                    text: `I added <b>${item.brand} ${item.subcategory}</b> - <b>${item.match?.supplier_name ?? ''} ${item.match?.name ?? ''} ${item.match?.code ?? ''}</b> to Invoice <b>${invoiceId}</b>`,
                    type: MessageType.RichText,
                    isMine: false,
                },
            ]);
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }
            if (axios.isAxiosError(error)) {
                toast.danger({
                    title: 'Failed to add item to invoice',
                    duration: 4000,
                });
            }

            runInAction(() => {
                this.addingToInvoice = false;
            });
        }
    };

    @action
    addPartToEstimate = async (
        item: ReplacementPartViewModelWithMatch,
        estimates: EstimateViewModel[]
    ) => {
        if (estimates.length === 0) {
            toast.danger({
                title: 'No estimate is selected.',
                duration: 4000,
            });
            return;
        }

        if (!item.match) {
            toast.danger({
                title: 'No Pricebook item found',
                duration: 4000,
            });
            return;
        }

        this.chatStore.enqueueMessages([
            {
                showAvatar: false,
                showBubble: true,
                date: new Date(),
                text: 'Add to Estimate',
                type: MessageType.Text,
                isMine: true,
            },
        ]);

        this.addingToEstimate = true;

        const requestData = { id: item.match.id };

        try {
            await Promise.all(
                estimates.map(estimate => {
                    return this.estimatesApi.addEstimateItem(estimate.id, requestData);
                })
            );

            runInAction(() => {
                this.addingToEstimate = false;
            });

            const estimateNames = estimates.map(estimate => estimate.label);
            this.chatStore.enqueueMessages([
                {
                    showAvatar: true,
                    showBubble: true,
                    text: `I added <b>${item.brand} ${item.subcategory}</b> - <b>${item.match?.supplier_name ?? ''} ${item.match?.name ?? ''} ${item.match?.code ?? ''}</b> to Estimate(s) <b>${estimateNames}</b>`,
                    type: MessageType.RichText,
                    isMine: false,
                },
            ]);
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }
            if (axios.isAxiosError(error)) {
                toast.danger({
                    title: 'Failed to add item to estimate',
                    duration: 4000,
                });
            }

            runInAction(() => {
                this.addingToEstimate = false;
            });
        }
    };

    @action
    addJobNote = async (item: ReplacementPartViewModelWithMatch) => {
        this.chatStore.enqueueMessages([
            {
                showAvatar: false,
                showBubble: true,
                date: new Date(),
                text: 'Create Job Note',
                type: MessageType.Text,
                isMine: true,
            },
        ]);

        const jobId = this.mainStore.jobDetails?.id;
        if (!jobId) {
            toast.danger({ title: 'Job not found.', duration: 4000 });
            return;
        }

        this.addingNoteToJob = true;
        const notePart0 = 'Done. I created the following job note for you:';
        const notePart1 = 'Replacement part identified. Please review and add to the job.';
        const notePart2 = `${item.brand} ${item.partNumber} - ${item.match?.supplier_name ?? ''} ${item.match?.name ?? ''} ${item.match?.code ?? ''}`;

        try {
            await this.jobsApi.addJobNote(jobId, { text: `${notePart1}\n${notePart2}` });

            runInAction(() => {
                this.addingNoteToJob = false;
            });

            this.chatStore.enqueueMessages([
                {
                    showAvatar: true,
                    showBubble: true,
                    text: `${notePart0}</br>"${notePart1}</br><strong>${notePart2}"</strong>`,
                    type: MessageType.RichText,
                    isMine: false,
                },
            ]);
        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }
            if (axios.isAxiosError(error)) {
                toast.danger({
                    title: 'Failed to create job note',
                    duration: 4000,
                });
            }

            runInAction(() => {
                this.addingNoteToJob = false;
            });
        }
    };
}
